import React, { createRef, useState, useRef, useEffect } from 'react';
import qs from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Language, LegalEntityRegistrationStorage, Utils } from '../../../utils';
import { user as userActions } from '../../../actions';
import { Button } from '../../../components';
import { Alert, AlertTitle } from '@material-ui/lab';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import Grid from '@material-ui/core/Grid';
import moment from 'moment';

import makeClasses from '../styles';

import clsx from 'clsx';
import { Input } from '@material-ui/core';

const TIMER_TIME = 60 * 5;

const StepTwo = ({ history, nextStep, prevStep, handleChange, stepOneData, stepOneEmail }) => {
  const dispatch = useDispatch();
  const classes = makeClasses();
  const searchParams = qs.parse(history.location.search);

  const { loading, languageTexts } = useSelector((state) => ({
    loading: state.user.loading,
    languageTexts: state.language.texts || {},
  }));

  const i18n = Language(languageTexts);

  const [attemptLimitReachedError, setAttemptLimitReachedError] = useState(false);
  const verificationCodeRefs = useRef(new Array(6).fill('').map(() => createRef()));
  const [verificationCode, setVerificationCode] = useState(new Array(6).fill(''));
  const [formattedTime, setFormattedTime] = useState('00:00');
  const [step2Completed, setStep2Completed] = useState(LegalEntityRegistrationStorage.getStep2Completed());
  const [timerStartTime, setTimerStartTime] = useState(LegalEntityRegistrationStorage.getStep2TimerStart());
  const [intervalId, setIntervalId] = useState(null);
  const [intervalTime, setIntervalTime] = useState(0);

  const canValidateVerificationCode = verificationCode.filter((code) => /[0-9]/.test(code)).length;

  const [affiliationStepTwoData, setAffiliationStepTwoData] = useState(
    searchParams.affiliationStepTwoData || LegalEntityRegistrationStorage.getStep2ChallengeId()
  );

  const _handleVerificationCodeOnBlur = (index) => (e) => {
    if (e.target.value.length > 0 && !/[0-9]/.test(e.target.value)) {
      const newVerificationCode = verificationCode.slice();
      newVerificationCode[index] = '';
      setVerificationCode(newVerificationCode);
      verificationCodeRefs.current[index].current.focus();
    }
  };

  const _handleVerificationCodeOnChange = (index) => (e) => {
    if (e.target.value.length === 0) {
      const newVerificationCode = verificationCode.slice();
      newVerificationCode[index] = '';
      return setVerificationCode(newVerificationCode);
    }

    if (!/[0-9]/.test(e.target.value)) {
      return;
    }

    const newVerificationCode = verificationCode.slice();
    newVerificationCode[index] = e.target.value.substring(0, 1);
    setVerificationCode(newVerificationCode);

    if (index + 1 < 6) {
      verificationCodeRefs.current[index + 1].current.focus();
    } else if (index === 5) {
      setVerificationCode(newVerificationCode);
    }
  };

  const _handleVerificationCondeOnPaste = (e) => {
    const pastedText = e.clipboardData.getData('text').trim();

    if (!/[0-9]{6}/.test(pastedText)) {
      return alert(i18n.get('Register.CodeInvalidCode'));
    }

    const newVerificationCode = verificationCode.slice();
    for (let i = 0, l = 6; i < l; i += 1) {
      newVerificationCode[i] = pastedText[i];
    }

    setVerificationCode(newVerificationCode);
  };

  const _requestNewVerificationCode = () => {
    setVerificationCode(new Array(6).fill(''));
    dispatch(
      userActions.merchantAffiliationRequestEmailVerificationCode(stepOneData, (affiliationStepTwoData) => {
        console.log(affiliationStepTwoData)
        if (affiliationStepTwoData === false) {
          setAttemptLimitReachedError(true);
        } else if (typeof affiliationStepTwoData === 'string') {
          setAffiliationStepTwoData(affiliationStepTwoData);
          LegalEntityRegistrationStorage.setStep2ChallengeId(affiliationStepTwoData);
          _startTimer();
        }
      })
    );
  };

  const _startTimer = () => {
    const now = Date.now();
    setTimerStartTime(now);
    LegalEntityRegistrationStorage.setStep2TimerStart(now);
  };

  const _stopTimer = () => {
    setTimerStartTime(null);
    LegalEntityRegistrationStorage.clearStep2();
    if (intervalId) {
      clearInterval(intervalId);
    }
    setIntervalId(null);
  };

  const _validateVerificationCode = () => {
    const hasValidVerificationCode = verificationCode.filter((code) => /[0-9]/.test(code)).length === 6;

    if (canValidateVerificationCode && hasValidVerificationCode) {
      dispatch(
        userActions.merchantAffiliationVerifyVerificationCode(
          stepOneData,
          affiliationStepTwoData,
          verificationCode.join(''),
          (result) => {
            if (result.result === 'SUCCESSFUL') {
              _stopTimer();
              setStep2Completed(true);
              LegalEntityRegistrationStorage.setStep2Completed(true);
            } else if (result.result === 'WRONG_ACCESS_CODE') {
              toast.error(i18n.get('LegalEntityRegistration.RegisterCodeValidationError'), { autoClose: 5000 });
            } else if (result.result === 'ATTEMPT_LIMIT_REACHED') {
              setAttemptLimitReachedError(true);
            } else if (result.result === 'EXPIRED_ACCESS_CODE') {
              _stopTimer();
              toast.error(i18n.get('LegalEntityRegistration.RegisterExpiredValidationCodeError'), { autoClose: 5000 });
            }
          }
        )
      );
    }
  };

  useEffect(() => _validateVerificationCode(), [canValidateVerificationCode]);

  useEffect(() => {
    if (timerStartTime) {
      const timer = TIMER_TIME - moment().diff(moment(timerStartTime), 's');
      if (timer < 0) {
        _stopTimer();
      } else {
        const minutes = Math.floor(timer / 60);
        const seconds = timer % 60;
        setFormattedTime(`${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`);
      }
    }
  }, [intervalTime, timerStartTime]);

  useEffect(() => {
    if (!intervalId) {
      const id = setInterval(() => {
        setIntervalTime((currentIntervalTime) => currentIntervalTime + 1);
      }, 1000);
      setIntervalId(id);
    }
  }, [intervalId]);

  useEffect(() => {
    if (step2Completed) {
      return;
    }
    if (stepOneData && !timerStartTime) {
      setVerificationCode(new Array(6).fill(''));
      verificationCodeRefs.current[0].current.focus();
      dispatch(
        userActions.merchantAffiliationRequestEmailVerificationCode(stepOneData, (stepTwoResponse) => {
          _startTimer();
          if (stepTwoResponse) {
            setAffiliationStepTwoData(stepTwoResponse);
            LegalEntityRegistrationStorage.setStep2ChallengeId(stepTwoResponse);
          }
        })
      );
    } else if (timerStartTime) {
      verificationCodeRefs.current[0].current.focus();
    }
  }, []);

  const handleNext = () => {
    nextStep({ data: stepOneData });
  };

  return (
    <React.Fragment>
      <div className={classes.formContent}>
        <div className={clsx(classes.formTitleContainer, classes.formTitleContainerWithAlert)}>
          <div className={classes.formTitle}>{i18n.get('LegalEntityRegistration.StepFormTitle')}</div>
          {!attemptLimitReachedError ? (
            <div className={classes.formText}>{i18n.get('LegalEntityRegistration.Step2FormText')}</div>
          ) : null}
        </div>
        {attemptLimitReachedError ? (
          <React.Fragment>
            <Alert severity="error" icon={false} className={clsx(classes.alert, classes.alertError)}>
              <div>
                <CancelOutlinedIcon className={classes.alertIcon} />
              </div>
              <div className={classes.alertMessage}>
                <AlertTitle className={classes.alertTitle}>
                  {i18n.get('LegalEntityRegistration.AlertAttemptLimitReachedErrorTitle')}
                </AlertTitle>
                {i18n.get('LegalEntityRegistration.AlertAttemptLimitReachedErrorMessage')}
              </div>
            </Alert>
          </React.Fragment>
        ) : step2Completed ? (
          <React.Fragment>
            <Alert severity="success" icon={false} className={clsx(classes.alert, classes.alertSuccess)}>
              <div>
                <CheckCircleOutlineOutlinedIcon className={classes.alertIcon} />
              </div>
              <div className={classes.alertMessage}>
                <AlertTitle className={classes.alertTitle}>
                  {i18n.get('LegalEntityRegistration.AlertSuccessStep2Title')}
                </AlertTitle>
                {i18n.get('LegalEntityRegistration.AlertSuccessStep2Message')}
              </div>
            </Alert>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Alert severity="warning" icon={false} className={clsx(classes.alert, classes.alertWarning)}>
              <div>
                <EmailOutlinedIcon className={classes.alertIcon} />
              </div>
              <div className={classes.alertMessage}>
                <AlertTitle className={classes.alertTitle}>
                  {i18n.get('LegalEntityRegistration.AlertWarningStep2Title').replace('{email}', Utils.maskEmail(stepOneEmail))}
                </AlertTitle>
                {i18n.get('LegalEntityRegistration.AlertWarningStep2Message1')}
                <span className={classes.alertTimer}>{formattedTime}</span>
                <span>{i18n.get('LegalEntityRegistration.AlertWarningStep2Message2')}</span>
              </div>
            </Alert>
            <form layout="vertical" className={clsx(classes.form, classes.formVerificationCode)}>
              <Grid container spacing={2} className={classes.verificationCodeGridContainer}>
                {verificationCode.map((code, index) => (
                  <Grid key={index} item xs={2} sm={2} md={2} className={classes.codeInputContainer}>
                    <Input
                      value={code}
                      onBlur={_handleVerificationCodeOnBlur(index)}
                      onChange={_handleVerificationCodeOnChange(index)}
                      inputProps={{ className: classes.verificationCodeInput, inputMode: 'numeric' }}
                      inputRef={verificationCodeRefs.current[index]}
                      onPaste={_handleVerificationCondeOnPaste}
                      disableUnderline={true}
                    />
                  </Grid>
                ))}
                {!step2Completed && <Grid xs={12}>
                  <Button
                    variant="outlined"
                    className={clsx(classes.resendCodeButton, timerStartTime ? classes.resendCodeButtonDisabled : null)}
                    onClick={_requestNewVerificationCode}
                    disabled={timerStartTime}
                  >
                    {i18n.get('Register.RequestNewVerificationCodeButton')}
                  </Button>
                </Grid>}
              </Grid>
            </form>
          </React.Fragment>
        )}
      </div>
      {!attemptLimitReachedError ? (
        <div
          className={clsx(
            classes.formSubmitContainer,
            step2Completed ? classes.formSubmitContainerWithOneButton : null
          )}
        >
          <Button className={classes.formSubmitButton} onClick={handleNext} disabled={loading || !step2Completed}>
            {i18n.get('LegalEntityRegistration.NextButton')}
          </Button>
        </div>
      ) : null}
    </React.Fragment>
  );
};
StepTwo.id = 'com.Handy.LegalEntityRegistration.StepTwo';
export default StepTwo;
